import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { profitMarginService } from '../../../services/financeService';
import { ProfitMarginAnalysis } from '../../../types/finance';
import { FaSave, FaTrash, FaPlus } from 'react-icons/fa';
import toast from 'react-hot-toast';

const MarginAnalysisTab: React.FC = () => {
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState<ProfitMarginAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState<ProfitMarginAnalysis>({
    name: '',
    product_name: '',
    selling_price: 0,
    cost_of_goods: 0,
    overhead_costs: 0,
    gross_margin: 0,
    net_margin: 0,
  });

  useEffect(() => {
    loadAnalyses();
  }, []);

  const loadAnalyses = async () => {
    try {
      setLoading(true);
      const data = await profitMarginService.getAll();
      setAnalyses(data);
    } catch (error) {
      console.error('Error loading profit margin analyses:', error);
      toast.error('Failed to load profit margin analyses');
    } finally {
      setLoading(false);
    }
  };

  const calculateMargins = (analysis: ProfitMarginAnalysis): { gross: number; net: number } => {
    const { selling_price, cost_of_goods, overhead_costs } = analysis;
    const grossMargin = selling_price > 0 ? (selling_price - cost_of_goods) / selling_price : 0;
    const netMargin = selling_price > 0 ? (selling_price - cost_of_goods - overhead_costs) / selling_price : 0;
    return {
      gross: parseFloat(grossMargin.toFixed(4)),
      net: parseFloat(netMargin.toFixed(4))
    };
  };

  const getMarginHealth = (margin: number): { status: string; color: string } => {
    if (margin <= 0) return { status: 'Loss', color: 'text-red-600' };
    if (margin < 0.15) return { status: 'Low', color: 'text-orange-500' };
    if (margin < 0.25) return { status: 'Average', color: 'text-yellow-500' };
    if (margin < 0.35) return { status: 'Good', color: 'text-green-500' };
    return { status: 'Excellent', color: 'text-green-700' };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let parsedValue: string | number = value;
    
    if (['selling_price', 'cost_of_goods', 'overhead_costs', 'industry_average_margin'].includes(name)) {
      parsedValue = parseFloat(value) || 0;
    }
    
    setCurrentAnalysis(prev => ({ ...prev, [name]: parsedValue }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { gross, net } = calculateMargins(currentAnalysis);
      const analysisToSave = {
        ...currentAnalysis,
        user_id: user?.id,
        gross_margin: gross,
        net_margin: net
      };
      
      if (currentAnalysis.id) {
        await profitMarginService.update(currentAnalysis.id, analysisToSave);
        toast.success('Analysis updated');
      } else {
        await profitMarginService.create(analysisToSave);
        toast.success('Analysis created');
      }
      
      setShowForm(false);
      setCurrentAnalysis({
        name: '',
        product_name: '',
        selling_price: 0,
        cost_of_goods: 0,
        overhead_costs: 0,
        gross_margin: 0,
        net_margin: 0,
      });
      loadAnalyses();
    } catch (error) {
      console.error('Error saving profit margin analysis:', error);
      toast.error('Failed to save analysis');
    }
  };

  const handleEdit = (analysis: ProfitMarginAnalysis) => {
    setCurrentAnalysis(analysis);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Delete this analysis?')) {
      try {
        await profitMarginService.delete(id);
        toast.success('Analysis deleted');
        loadAnalyses();
      } catch (error) {
        console.error('Error deleting analysis:', error);
        toast.error('Failed to delete analysis');
      }
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading analyses...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Info Box */}
      <div className="rounded-xl bg-green-50 p-5 shadow-sm ring-1 ring-green-100">
        <h3 className="mb-2 text-base font-semibold text-green-900">Profit Margin Analysis</h3>
        <p className="mb-3 text-sm text-green-800">
          Analyze your product profitability by calculating gross and net margins.
        </p>
        <ul className="space-y-1 text-sm text-green-700">
          <li><strong>Gross Margin:</strong> Revenue after direct costs (COGS)</li>
          <li><strong>Net Margin:</strong> Revenue after all expenses</li>
          <li><strong>Healthy margins:</strong> Retail 20-25%, Manufacturing 25-35%, Software 70-80%</li>
        </ul>
      </div>

      {/* Create Button */}
      {!showForm && (
        <button
          onClick={() => {
            setCurrentAnalysis({
              name: '',
              product_name: '',
              selling_price: 0,
              cost_of_goods: 0,
              overhead_costs: 0,
              gross_margin: 0,
              net_margin: 0,
            });
            setShowForm(true);
          }}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
        >
          <FaPlus /> Create New Analysis
        </button>
      )}

      {/* Form */}
      {showForm && (
        <div className="rounded-xl bg-gray-50 p-6 shadow-sm ring-1 ring-gray-200">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            {currentAnalysis.id ? 'Edit' : 'Create'} Margin Analysis
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Analysis Name *</label>
                <input
                  type="text"
                  name="name"
                  value={currentAnalysis.name}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="e.g., Q1 2025 Product Line"
                  required
                />
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Product/Service *</label>
                <input
                  type="text"
                  name="product_name"
                  value={currentAnalysis.product_name}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="e.g., Premium Subscription"
                  required
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Selling Price ($) *</label>
                <input
                  type="number"
                  name="selling_price"
                  value={currentAnalysis.selling_price}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="29.99"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Cost of Goods ($) *</label>
                <input
                  type="number"
                  name="cost_of_goods"
                  value={currentAnalysis.cost_of_goods}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="10.00"
                  min="0"
                  step="0.01"
                  required
                />
                <span className="mt-1 block text-xs text-gray-500">Materials & labor</span>
              </div>
              
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Overhead ($) *</label>
                <input
                  type="number"
                  name="overhead_costs"
                  value={currentAnalysis.overhead_costs}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="5.00"
                  min="0"
                  step="0.01"
                  required
                />
                <span className="mt-1 block text-xs text-gray-500">Rent, utilities, etc.</span>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Industry Average Margin</label>
              <input
                type="number"
                name="industry_average_margin"
                value={currentAnalysis.industry_average_margin || ''}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="0.25"
                min="0"
                max="1"
                step="0.01"
              />
              <span className="mt-1 block text-xs text-gray-500">As decimal (e.g., 0.25 for 25%)</span>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                name="notes"
                value={currentAnalysis.notes || ''}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="Additional information"
                rows={2}
              />
            </div>

            {/* Margin Preview */}
            {currentAnalysis.selling_price > 0 && (
              <div className="rounded-xl bg-green-50 p-5 shadow-sm ring-1 ring-green-200">
                <h4 className="mb-3 text-base font-semibold text-green-900">Margin Preview</h4>
                
                {(() => {
                  const { gross, net } = calculateMargins(currentAnalysis);
                  const grossHealth = getMarginHealth(gross);
                  const netHealth = getMarginHealth(net);
                  const cogs = currentAnalysis.cost_of_goods / currentAnalysis.selling_price;
                  const overhead = currentAnalysis.overhead_costs / currentAnalysis.selling_price;
                  
                  return (
                    <>
                      <div className="mb-4 grid gap-3 md:grid-cols-2">
                        <div>
                          <p className="text-sm text-gray-700">
                            <strong>Gross Margin:</strong> 
                            <span className={`ml-2 text-lg font-bold ${grossHealth.color}`}>
                              {(gross * 100).toFixed(1)}%
                            </span>
                            <span className={`ml-2 text-sm ${grossHealth.color}`}>({grossHealth.status})</span>
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-700">
                            <strong>Net Margin:</strong> 
                            <span className={`ml-2 text-lg font-bold ${netHealth.color}`}>
                              {(net * 100).toFixed(1)}%
                            </span>
                            <span className={`ml-2 text-sm ${netHealth.color}`}>({netHealth.status})</span>
                          </p>
                        </div>
                      </div>

                      {/* Visual Breakdown */}
                      <div>
                        <p className="mb-2 text-xs font-medium text-gray-700">Revenue Breakdown (per $1.00)</p>
                        <div className="h-6 w-full overflow-hidden rounded-full bg-gray-200">
                          <div className="flex h-full">
                            <div 
                              className="flex items-center justify-center bg-red-500 text-xs text-white"
                              style={{ width: `${cogs * 100}%` }}
                              title={`COGS: $${currentAnalysis.cost_of_goods.toFixed(2)}`}
                            >
                              {cogs >= 0.1 && `${(cogs * 100).toFixed(0)}%`}
                            </div>
                            <div 
                              className="flex items-center justify-center bg-yellow-500 text-xs text-white"
                              style={{ width: `${overhead * 100}%` }}
                              title={`Overhead: $${currentAnalysis.overhead_costs.toFixed(2)}`}
                            >
                              {overhead >= 0.1 && `${(overhead * 100).toFixed(0)}%`}
                            </div>
                            <div 
                              className="flex items-center justify-center bg-green-500 text-xs text-white"
                              style={{ width: `${net * 100}%` }}
                              title={`Profit: $${(currentAnalysis.selling_price - currentAnalysis.cost_of_goods - currentAnalysis.overhead_costs).toFixed(2)}`}
                            >
                              {net >= 0.1 && `${(net * 100).toFixed(0)}%`}
                            </div>
                          </div>
                        </div>
                        <div className="mt-1 flex justify-between text-xs text-gray-600">
                          <span>COGS</span>
                          <span>Overhead</span>
                          <span>Profit</span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2">
              {currentAnalysis.id && (
                <button
                  type="button"
                  onClick={() => handleDelete(currentAnalysis.id!)}
                  className="flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                >
                  <FaTrash /> Delete
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
              >
                <FaSave /> {currentAnalysis.id ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Analyses Table */}
      {analyses.length > 0 && (
        <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Price</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">COGS</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Gross Margin</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Net Margin</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {analyses.map((analysis) => {
                  const grossHealth = getMarginHealth(analysis.gross_margin);
                  const netHealth = getMarginHealth(analysis.net_margin);
                  
                  return (
                    <tr key={analysis.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{analysis.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{analysis.product_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">${analysis.selling_price.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">${analysis.cost_of_goods.toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={grossHealth.color}>
                          {(analysis.gross_margin * 100).toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={netHealth.color}>
                          {(analysis.net_margin * 100).toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(analysis)}
                            className="text-primary hover:underline"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(analysis.id!)}
                            className="text-red-600 hover:underline"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {analyses.length === 0 && !showForm && (
        <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
          <div className="text-4xl mb-4">📊</div>
          <h3 className="text-lg font-semibold text-gray-900">No Margin Analyses Yet</h3>
          <p className="mt-2 text-sm text-gray-600">
            Create your first analysis to start tracking profit margins
          </p>
        </div>
      )}
    </div>
  );
};

export default MarginAnalysisTab;
